import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_URL } from '../../Constants'; // Restored for the config API call
import { AuthContext } from '../context/AuthContext';

const WhatsAppTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const { user } = useContext(AuthContext);
  const token = user?.token || localStorage.getItem("token");
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  // Helper function to transform API data to our structure
  const transformTemplateData = (apiData) => {
    // Check if apiData is an array to avoid map errors
    if (!Array.isArray(apiData)) return [];

    return apiData.map(template => {
      let header = '';
      let body = '';
      let footer = '';
      const buttons = [];
      
      // Safety check in case components array is missing
      if (template.components && Array.isArray(template.components)) {
        template.components.forEach(component => {
          switch(component.type) {
            case 'HEADER':
              header = component.text || '';
              break;
            case 'BODY':
              body = component.text || '';
              break;
            case 'FOOTER':
              footer = component.text || '';
              break;
            case 'BUTTONS':
              if (component.buttons) {
                buttons.push(...component.buttons);
              }
              break;
            default:
              break;
          }
        });
      }
      
      return {
        id: template.id || Math.random().toString(),
        name: template.name,
        category: template.category ? template.category.toLowerCase() : 'uncategorized',
        language: template.language || 'en_US',
        header: header,
        body: body,
        footer: footer,
        buttons: buttons,
        status: template.status === 'APPROVED' || template.active ? 'approved' : 'pending',
        active: template.active !== undefined ? template.active : true
      };
    });
  };

  // Fetch configuration and templates
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!companyId) {
        setError('Company ID is missing.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // 1. Fetch the WhatsApp configuration for this company
        const configResponse = await axios.get(
          `${API_URL}/api/whatsappConfig/${companyId}`, 
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        const configData = configResponse.data;
        
        if (configData.status !== 'success' || !configData.data) {
          throw new Error('Failed to retrieve WhatsApp configuration.');
        }

        const { domain, baseUrl, apiKey } = configData.data;

        if (!domain || !baseUrl || !apiKey) {
          throw new Error('Configuration is missing domain, baseUrl, or apiKey.');
        }

        // 2. Construct the template API URL avoiding double slashes
        const cleanDomain = domain.replace(/\/$/, '');
        const cleanBaseUrl = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
        const templateApiUrl = `${cleanDomain}${cleanBaseUrl}/getTemplate`;

        // 3. Fetch templates using the constructed URL
        const templateResponse = await axios.get(templateApiUrl, {
          params: { apiKey: apiKey },
        });
        
        // 4. Transform and set data
        const templateData = transformTemplateData(templateResponse.data);
        setTemplates(templateData);
        setLoading(false);

      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch templates. Please try again later.');
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [companyId, token]);

  // View template details
  const viewTemplateDetails = (template) => {
    setSelectedTemplate(template);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    switch (category) {
      case 'marketing': return 'bg-purple-100 text-purple-800';
      case 'utility': return 'bg-blue-100 text-blue-800';
      case 'authentication': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">WhatsApp Template Manager</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.length > 0 ? (
          templates.map(template => (
            <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-800 truncate">{template.name}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                    {template.status}
                  </span>
                </div>
                
                <div className="mb-4 flex justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(template.category)}`}>
                    {template.category}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {template.language}
                  </span>
                </div>

                {template.header && (
                  <div className="mb-3 p-2 bg-gray-100 rounded">
                    <p className="text-sm text-gray-600">Header: {template.header}</p>
                  </div>
                )}

                <div className="mb-3 p-2 bg-gray-50 rounded">
                  <p className="text-sm text-gray-700 line-clamp-4">{template.body}</p>
                </div>

                {template.footer && (
                  <div className="mb-3 p-2 bg-gray-100 rounded">
                    <p className="text-sm text-gray-600">Footer: {template.footer}</p>
                  </div>
                )}

                {template.buttons.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Buttons:</p>
                    <div className="flex flex-wrap gap-1">
                      {template.buttons.map((button, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {button.text}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center mt-4">
                  <button
                    onClick={() => viewTemplateDetails(template)}
                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-10">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
          </div>
        )}
      </div>

      {/* Template Detail Modal */}
      {showModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold text-gray-900">
                Template Details
              </h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={closeModal}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Name</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Category</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.category}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.status}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Language</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.language}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Active</p>
                  <p className="text-sm text-gray-900">{selectedTemplate.active ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {selectedTemplate.header && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Header</p>
                  <div className="mt-1 p-3 bg-gray-100 rounded-md">
                    <p className="text-sm text-gray-700">{selectedTemplate.header}</p>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-500">Body</p>
                <div className="mt-1 p-3 bg-gray-100 rounded-md">
                  <p className="text-sm text-gray-700 whitespace-pre-line">{selectedTemplate.body}</p>
                </div>
              </div>

              {selectedTemplate.footer && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Footer</p>
                  <div className="mt-1 p-3 bg-gray-100 rounded-md">
                    <p className="text-sm text-gray-700">{selectedTemplate.footer}</p>
                  </div>
                </div>
              )}

              {selectedTemplate.buttons.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Buttons</p>
                  <div className="mt-2 space-y-2">
                    {selectedTemplate.buttons.map((button, index) => (
                      <div key={index} className="flex items-center p-2 bg-gray-100 rounded-md">
                        <span className="text-xs font-medium text-gray-700 mr-2">Type: {button.type}</span>
                        <span className="text-xs text-gray-700">Text: {button.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b">
              <button
                onClick={closeModal}
                type="button"
                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplate;